import { TextAreaInput } from '@/components/form/TextAreaInput';
import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { Feature } from '@/services/backstage/Feature';
import {
  ApprovalState,
  useApproveWorkingPatternMutation,
  useRejectWorkingPatternMutation
} from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { useWorkingPatternRequests } from '@/services/workingPatterns/useWorkingPatternRequests';
import { FunctionComponent, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { ScheduleChangeRequestDetailsCard } from './TeamMemberScheduleChangesDetailsCard';

const TeamMemberRequestScheduleUpdateViewDetailsPage: FunctionComponent = () => {
  const { userId, requestId } = useParams<'userId' | 'requestId'>();
  const navigate = useNavigate();
  const { request, refetch } = useWorkingPatternRequests(userId, requestId);
  const [approveResult, approveWorkingPattern] = useApproveWorkingPatternMutation();
  const [rejectResult, rejectWorkingPattern] = useRejectWorkingPatternMutation();
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const isPending = request?.state === ApprovalState.Requested;
  const isUpdating = approveResult.fetching || rejectResult.fetching;
  const [rejectionReason, setRejectionReason] = useState('');

  const approveRequest = async () => {
    if (!userId || !isPending || isUpdating) return;

    const result = await approveWorkingPattern({ workingPatternApproval: { employeeId: userId } });

    await handleMutationResult(result, {
      successMessage: 'Schedule request approved.',
      onSuccess: async () => {
        await refetch({ requestPolicy: 'network-only' });
        setTimeout(() => navigate(-1), 100);
      }
    });
  };

  const rejectRequest = async () => {
    if (!userId || !isPending || isUpdating) return;

    const result = await rejectWorkingPattern({
      workingPatternRejection: { employeeId: userId, rejectionReason: rejectionReason.trim() || undefined }
    });

    await handleMutationResult(result, {
      successMessage: 'Schedule request denied.',
      onSuccess: async () => {
        await refetch({ requestPolicy: 'network-only' });
        setTimeout(() => navigate(-1), 100);
      }
    });
  };

  if (!request) {
    return <p className="p-6">Schedule update request not found.</p>;
  }

  return (
    <Feature name="requests">
      <PageContainer>
        <PageSizer mode={SizerMode.PAGE} className="justify-items-center">
          <TSTitleCard
            className="w-full space-y-5 p-5"
            title={strings.timeshift.teamMember.requests.scheduleUpdate.cardTitle}
            icon={FiBell}
          >
            <ScheduleChangeRequestDetailsCard
              request={request}
              actions={
                <div className="flex space-x-4 justify-end">
                  <Button big onClick={() => navigate(-1)}>
                    {strings.common.back}
                  </Button>
                  {isPending && (
                    <>
                      <Button big danger disabled={isUpdating} onClick={() => setRejectDialogOpen(true)}>
                        {strings.common.deny}
                      </Button>
                      <Button big primary disabled={isUpdating} onClick={() => setApproveDialogOpen(true)}>
                        {strings.common.approve}
                      </Button>
                    </>
                  )}
                </div>
              }
            />
          </TSTitleCard>
        </PageSizer>
      </PageContainer>
      <ConfirmDialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={approveRequest}
        title="Approve schedule request"
        confirmLabel={strings.common.approve}
        cancelLabel={strings.common.cancel}
        loading={approveResult.fetching}
      >
        Are you sure you want to approve this schedule request?
      </ConfirmDialog>
      <ConfirmDialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectionReason('');
        }}
        onConfirm={rejectRequest}
        title="Deny schedule request"
        confirmLabel={strings.common.deny}
        cancelLabel={strings.common.cancel}
        dangerous
        loading={rejectResult.fetching}
      >
        <div className="space-y-3">
          <p>Are you sure you want to deny this schedule request?</p>
          <div>
            <label htmlFor="rejection-reason" className="text-sm font-medium block mb-1">
              Reason (optional)
            </label>
            <TextAreaInput
              name="rejection-reason"
              placeholder="Let them know why this request is being denied…"
              value={rejectionReason}
              onChange={setRejectionReason}
              rows={3}
            />
          </div>
        </div>
      </ConfirmDialog>
    </Feature>
  );
};

export default TeamMemberRequestScheduleUpdateViewDetailsPage;
